import { ILigaSubscription } from '@/model/liga-subscription.model';
import { ILigaSubscriptionPurchase } from '@/model/liga-subscription-purchase.model';
import { phoneForApi } from '@/utils/phone.util';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { FileTransfer } from '@capacitor/file-transfer';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { isPlatform } from '@ionic/vue';
import axios from 'axios';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const getToken = async (): Promise<string> => {
  const { value } = await Preferences.get({ key: 'jwt' });
  return value || '';
};

const baseUrl = () => process.env.VUE_APP_API_URL || '';

const api = async <T>(method: string, path: string, body?: unknown, params?: Record<string, string>): Promise<T> => {
  const token = await getToken();
  const url = `${baseUrl()}api${path}`;
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  if (isPlatform('ios') || isPlatform('android')) {
    const opts: any = { url, method, headers, readTimeout: 60000 };
    if (body != null) {
      headers['Content-Type'] = 'application/json';
      opts.data = JSON.stringify(body);
    }
    if (params) opts.params = params;
    const res = await CapacitorHttp.request(opts);
    if (res.status >= 400) throw new Error((res.data as any)?.message || `HTTP ${res.status}`);
    return res.data as T;
  }
  const config: any = { url: `api${path}`, method, headers };
  if (body) config.data = body;
  if (params) config.params = params;
  const { data } = await axios.request<T>(config);
  return data;
};

export default {
  async getSubscriptions(options?: boolean | { all?: boolean }): Promise<ILigaSubscription[]> {
    let params: Record<string, string> | undefined;
    if (typeof options === 'boolean') {
      params = options ? { all: 'true' } : undefined;
    } else if (options?.all) {
      params = { all: 'true' };
    }
    return api<ILigaSubscription[]>('GET', '/liga-subscriptions', undefined, params);
  },

  async getSubscription(id: number): Promise<ILigaSubscription | null> {
    try {
      return await api<ILigaSubscription>('GET', `/liga-subscriptions/${id}`);
    } catch {
      return null;
    }
  },

  async getMyPurchases(): Promise<ILigaSubscriptionPurchase[]> {
    return api<ILigaSubscriptionPurchase[]>('GET', '/liga-subscriptions/my-purchases');
  },

  async syncMyPayment(subscriptionId: number): Promise<boolean> {
    try {
      const result = await api<{ paid?: boolean }>('GET', '/liga-subscriptions/sync-my-payment', undefined, {
        subscriptionId: String(subscriptionId),
      });
      return result?.paid === true;
    } catch {
      return false;
    }
  },

  async getMyPhone(): Promise<string> {
    const response = await api<{ phone?: string }>('GET', '/liga-subscriptions/my-phone');
    return response?.phone ?? '';
  },

  async purchase(
    subscriptionId: number,
    priceTierId?: number,
    phone?: string,
    options?: { renew?: boolean; promoCode?: string }
  ): Promise<ILigaSubscriptionPurchase> {
    const body: { priceTierId?: number; phone?: string; renew?: boolean; promoCode?: string } = {};
    if (priceTierId != null) body.priceTierId = priceTierId;
    const normalizedPhone = phoneForApi(phone);
    if (normalizedPhone) body.phone = normalizedPhone;
    if (options?.renew) body.renew = true;
    if (options?.promoCode?.trim()) body.promoCode = options.promoCode.trim();
    return api<ILigaSubscriptionPurchase>('POST', `/liga-subscriptions/${subscriptionId}/purchase`, body);
  },

  async previewPromo(
    subscriptionId: number,
    promoCode: string,
    priceTierId?: number
  ): Promise<{
    code: string;
    percentOff: number;
    listAmountKopecks: number;
    discountAmountKopecks: number;
    amountPaidKopecks: number;
  }> {
    const body: { promoCode: string; priceTierId?: number } = { promoCode };
    if (priceTierId != null) body.priceTierId = priceTierId;
    return api('POST', `/liga-subscriptions/${subscriptionId}/preview-promo`, body);
  },

  async cancelPurchase(purchaseId: number): Promise<void> {
    await api('DELETE', `/liga-subscriptions/purchases/${purchaseId}`);
  },

  async createPayment(
    purchaseId: number,
    priceTierId?: number,
    customerEmail?: string,
    phone?: string,
    promoCode?: string
  ): Promise<{ paymentId: string | null; redirectUrl: string | null; amountKopecks: number }> {
    const body: { priceTierId?: number; customerEmail?: string; phone?: string; returnToApp?: boolean; promoCode?: string } = {};
    if (priceTierId != null) body.priceTierId = priceTierId;
    if (customerEmail != null && customerEmail.trim()) body.customerEmail = customerEmail.trim();
    const normalizedPhone = phoneForApi(phone);
    if (normalizedPhone) body.phone = normalizedPhone;
    if (promoCode?.trim()) body.promoCode = promoCode.trim();
    if (Capacitor.isNativePlatform()) body.returnToApp = true;
    return api('POST', `/liga-subscriptions/purchases/${purchaseId}/create-payment`, Object.keys(body).length ? body : undefined);
  },

  logoUrl(logoPath: string | undefined, width?: number): string {
    if (!logoPath) return '';
    const base = baseUrl().replace(/\/$/, '');
    let url = `${base}/api/liga-subscriptions/logo?path=${encodeURIComponent(logoPath)}`;
    if (width != null && width > 0) {
      url += `&width=${width}`;
    }
    return url;
  },

  async createSubscription(subscription: Partial<ILigaSubscription>): Promise<ILigaSubscription> {
    return api<ILigaSubscription>('POST', '/liga-subscriptions', subscription);
  },

  async updateSubscription(id: number, subscription: Partial<ILigaSubscription>): Promise<ILigaSubscription> {
    return api<ILigaSubscription>('PUT', `/liga-subscriptions/${id}`, { ...subscription, id });
  },

  async deleteSubscription(id: number): Promise<void> {
    await api('DELETE', `/liga-subscriptions/${id}`);
  },

  async setPriceTiers(
    subscriptionId: number,
    tiers: { id?: number; name: string; amountKopecks: number; requiredAuthorityName?: string | null }[]
  ): Promise<ILigaSubscription['priceTiers']> {
    return api('PUT', `/liga-subscriptions/${subscriptionId}/price-tiers`, tiers);
  },

  async setGiftAuthorities(subscriptionId: number, authorityNames: string[]): Promise<string[]> {
    return api('PUT', `/liga-subscriptions/${subscriptionId}/gift-authorities`, authorityNames);
  },

  async uploadLogo(subscriptionId: number, file: File): Promise<void> {
    const token = await getToken();
    const base = baseUrl().replace(/\/$/, '');
    const url = `${base}/api/liga-subscriptions/${subscriptionId}/logo`;

    if (isPlatform('ios') || isPlatform('android')) {
      const ext = file.name.split('.').pop() || 'png';
      const tempPath = `sub_logo_upload_${Date.now()}.${ext}`;
      try {
        const base64 = await fileToBase64(file);
        await Filesystem.writeFile({
          path: tempPath,
          data: base64,
          directory: Directory.Cache,
        });
        const { uri } = await Filesystem.getUri({
          path: tempPath,
          directory: Directory.Cache,
        });
        const result = await FileTransfer.uploadFile({
          url,
          path: uri,
          fileKey: 'file',
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 60000,
          connectTimeout: 60000,
        });
        const code = Number(result.responseCode);
        if (code >= 400) {
          let msg = `HTTP ${code}`;
          if (result.response) {
            try {
              const body = JSON.parse(result.response);
              if (body?.message) msg = body.message;
            } catch {
              // ignore
            }
          }
          throw new Error(msg);
        }
      } finally {
        try {
          await Filesystem.deleteFile({
            path: tempPath,
            directory: Directory.Cache,
          });
        } catch {
          // ignore
        }
      }
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    await axios.post(`api/liga-subscriptions/${subscriptionId}/logo`, formData, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 60000,
    });
  }
};
